import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useUserSession } from '../../models/hooks/appStateHooks';

/** The API's default when the account hasn't set a send-link window. */
export const DEFAULT_SEND_FILE_LINK_EXPIRATION_DAYS = 90;

export interface ICreateSendFileLinkParams {
	/** Files already on the server (the contact's saved files), by attachment id. */
	attachmentIds?: string[];
	contactId: string;
	description?: string;
	/** Files picked from the computer; uploaded first, then linked. */
	files?: File[];
	title?: string;
}

/**
 * Creates a secure download link for one contact up front (`POST sharedFileLink`), so it can carry a title and
 * description, and resolves with its short link. New files are uploaded through `POST email/attachment` first.
 * Uploads are remembered per file, so retrying after a partial failure only uploads what is still missing.
 */
export function useCreateSendFileLinkMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const uploadedIds = useRef(new Map<File, string>());

	return useMutation({
		mutationFn: async ({
			attachmentIds = [],
			contactId,
			description,
			files = [],
			title,
		}: ICreateSendFileLinkParams) => {
			const pending = files.filter(file => !uploadedIds.current.has(file));
			const results = await Promise.allSettled(
				pending.map(file => {
					const data = new FormData();
					data.append('file', file);
					return userSession.webServiceHelper.callAsync<Api.IFileAttachment>(
						Api.ImpersonationBroker.composeApiUrl({ urlPath: 'email/attachment' }),
						'POST',
						data
					);
				})
			);
			results.forEach((result, i) => {
				if (result.status === 'fulfilled') {
					uploadedIds.current.set(pending[i], result.value.id);
				}
			});
			const failed = pending.filter(file => !uploadedIds.current.has(file));
			if (failed.length) {
				const systemMessage = `${failed.map(f => f.name).join(', ')} didn’t upload. Try again.`;
				// Carries the operation-result fields the dialogs read, so the user sees which files to retry.
				throw Object.assign(new Error(systemMessage), { systemCode: 400, systemMessage });
			}
			const expirationDays =
				userSession.account?.features?.files?.defaultSendFileLinkExpirationDays ??
				DEFAULT_SEND_FILE_LINK_EXPIRATION_DAYS;
			const expiration = new Date(Date.now() + expirationDays * 24 * 60 * 60 * 1000).toISOString();

			const link = await userSession.webServiceHelper.callAsync<{ shortLink?: string }>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: 'sharedFileLink' }),
				'POST',
				{
					contactId,
					description: description?.trim() || undefined,
					expiration,
					fileAttachments: [...attachmentIds, ...files.map(file => uploadedIds.current.get(file))].map(id => ({ id })),
					notifyOnDownload: true,
					title: title?.trim() || undefined,
				}
			);
			return link?.shortLink;
		},
		onSuccess: (_link, { contactId }) => {
			uploadedIds.current.clear();
			queryClient.invalidateQueries({ queryKey: ['SharedFile/byContact', contactId] });
		},
	});
}
